import React, { useState, useEffect } from 'react'

import {
  StyleSheet,
  View,
  Animated,
  Platform,
  Text,
  Dimensions
} from 'react-native'
import _ from "lodash"

import { useContent } from '@sart/content'

import client, { useClient } from '@sart/client'
import {
  adaptive,
  colors,
  backgroundColors,
  typography,
  radius,
  shadows
} from '@sart/styles'
import {
  Space,
  MoonDial,
  ProductAddon,
  BasePlan,
  ProductRecurring
} from '@sart/components'
import {
  broadbandBalanceContainer,
  setupBroadbandBalance
} from '@sart/utilities'
//import { CompanyIcon, WiFi } from '@sart/icons'
import { CompanyIcon } from '@sart/icons'

const BRANDING_HEIGHT = 24
const MAX_DIALS_HEIGHT = 120
const MIN_DIALS_HEIGHT = 48
const EXPIRATION_HEIGHT = 24
const HALF_PADDING = 12
export default function BroadbandDashboard(props) {
  const { account, service, navigation, reloadData } = props
  const content = useContent()
  const [data, setData] = useState({})
  const [latestInvoice, setLatestInvoice] = useState({})
  const [balance, setBalance] = useState({})
  const balanceContainer = _.cloneDeep(broadbandBalanceContainer)

  const {
    cache: {
      loading,
      customer,
			language
    } = {}
  } = useClient()

  useEffect(() => {
    loadBalancesAndInvoices()
    loadProducts()
    loadCustomerData()
  }, [])

  async function loadBalancesAndInvoices() {
    try {
      let balanceData = await client.getAccountBalances(account,service)
      let filteredBalance = setupBroadbandBalance(balanceContainer, balanceData.balances)
      setBalance(filteredBalance[0])
      setLatestInvoice(balanceData.latestInvoice)
			client.setCache({
				'broadbandBalance':filteredBalance[0]
			})
			client.setCache({'broadbandBalance':filteredBalance[0]})
    } catch (error) {
      // console.log(error)
    }
  }

  async function loadProducts() {
    try {
        let productData = await client.getProvisionedProductsForService(account,service)
        setData(productData)
    } catch (error) {
        console.log(error)
    }
}

  function cancelRecurringAddOn(addOn) {
    //set up the basket with the del item
    // const order = client.transactionServiceDelItem(addOn)
    // if (order.status === 'successful) {
    // console.log('first addOn is: ');
    // console.log(addOn);
    // const confirmation = {
    //   title: `${addOn.product.productName}`,
    //   description: `${content.dashboard.cancelDescription}`
    // }
    const { instanceLegacyId: instanceId } = addOn
    navigation.navigate('ConfirmCancel', { addOn, instanceId })
    //}
  }

  function loadCustomerData() {
    //If the customer has not been loaded yet, load it
    //Mainly here for web app refresh issue SRPRODDEV-386
    if (customer === undefined) {
      client.loadPersonalDetails()
    }
  }

  function getBalanceQuantity(balance) {
    if (balance !== undefined) {
      return balance.displayBalanceUnits !== undefined ? balance.displayBalanceUnits : 0
    }
    return 0
  }

  function getBalanceStartingQuantity(balance) {
    if (balance !== undefined) {
      return balance.displayStartingBalanceUnits !== undefined ? balance.displayStartingBalanceUnits : 0
    }
    if (balance < 0) {
      return balance.displayStartingBalanceUnits < 0 ? balance.displayStartingBalanceUnits : 0
    }
    return 0
  }

  const UNITS = [
    'B',
    'KB',
    'MB',
    'GB',
    'TB',
    'PB',
    'EB',
    'ZB',
    'YB'
  ];

  const toLocaleString = (number, locale) => {
    let result = number;
    if (typeof locale === 'string') {
      result = number.toLocaleString(locale);
    } else if (locale === true) {
      result = number.toLocaleString();
    }

    return result;
  };

  function getConversionRateData(balance) {
    const conversionRates = balance.balanceDefinition.conversionRates
    var rate = conversionRates.filter(function (rate) {
      return rate.unit === 'GB'
    })
    var toReturn = rate[0].rate === undefined ? 1000 : rate[0].rate
    return toReturn
  }

  function formatBytes(value, balance, options) {
    if (!Number.isFinite(value)) {
      throw new TypeError(`Expected a finite number, got ${typeof value}: ${value}`);
    }

    options = Object.assign({}, options);

    if (value === 0) {
      return ' 0B';
    }

    const isNegative = value < 0;
    const prefix = isNegative ? '-' : '';

    if (isNegative) {
      return value;
    }

    if (value < 1) {
      const numberString = toLocaleString(value, options.locale);
      return prefix + numberString + 'B';
    }

    //const exponent = Math.min((Math.floor(Math.log10(value) / 3) + 1), UNITS.length - 1);
    //value = Number((value / getConversionRateData(balance)).toPrecision(2));
    const numberString = toLocaleString(value, options.locale);

    const unit = 'GB';

    return prefix + numberString + '' + unit;

  }

  const MAX_HEADER_HEIGHT = BRANDING_HEIGHT + MAX_DIALS_HEIGHT + EXPIRATION_HEIGHT
  const MIN_HEADER_HEIGHT = MIN_DIALS_HEIGHT
  const HEADER_HEIGHT_DELTA = MAX_HEADER_HEIGHT - MIN_HEADER_HEIGHT

  const scrollY = new Animated.Value(0)

  const headerY = scrollY.interpolate({
    inputRange: [0, HEADER_HEIGHT_DELTA],
    outputRange: [0, 1],
    extrapolateRight: 'clamp' // Animated.Extrapolate.CLAMP
  })

  const translateY = headerY.interpolate({
    inputRange: [0, 1],
    outputRange: [0, 36],
    extrapolateLeft: 'clamp' // Animated.Extrapolate.CLAMP
  })

  const headerHeight = headerY.interpolate({
    inputRange: [0, 1],
    outputRange: [MAX_HEADER_HEIGHT, MIN_HEADER_HEIGHT],
    extrapolateLeft: 'clamp' // Animated.Extrapolate.CLAMP
  })

  const spacerHeight = headerY.interpolate({
    inputRange: [0, 1],
    outputRange: [0, HEADER_HEIGHT_DELTA],
  })

  const hide = headerY.interpolate({
    inputRange: [0, 0.7],
    outputRange: [1, 0],
  })

  const show = headerY.interpolate({
    inputRange: [0.7, 1],
    outputRange: [0, 1],
  })

  const smallDialSize = headerY.interpolate({
    inputRange: [0, 1],
    outputRange: [86, 48],
    extrapolate: 'clamp'
  })

  const bigDialSize = 172

  return (
    <View>
      <Animated.ScrollView
        scrollEventThrottle={1}
        // stickyHeaderIndices={[0,]}
        showsVerticalScrollIndicator={true}
        style={styles.container}
        onScroll={
          Animated.event([
            { nativeEvent: { contentOffset: { y: scrollY } } }
          ],{})
        }
      >
        <View
          style={styles.headerContainer}
        >
          <Space height={48} />
          <Animated.View
            style={[styles.headerContent, { height: headerHeight }]}
          >
            <Animated.View
              style={[styles.branding, { transform: [{ translateY: translateY }] }]}
            >
            </Animated.View>

            {Platform.OS === 'ios' &&
              <Space height={24} />
            }
            <View style={styles.rowContainer}>
              <View style={{ justifyContent: 'flex-end' }}>
                <CompanyIcon color={colors.srGreen} height={60} width={60} />
              </View>
              {latestInvoice && 
                <View style={styles.amountDue}>
                    <Space height={12} />
                    <Text style={styles.amountDueTitle}>{content.dashboard.amountDue}</Text>
                    <Text style={styles.amountDueText}>{content.common.currency}{(latestInvoice.outstandingAmountDue / 100).toFixed(2)}</Text>
                </View>
                }
                {!latestInvoice &&
                <View style={styles.amountDue}>
                    <Space height={12} />
                    <Text style={styles.amountDueTitle}>{content.dashboard.invoiceLoading}</Text>
                </View>
                }
            </View>

            <View style={styles.headerMain}>
              <View>
                <View>
                  <Space height={12} />
                  <View style={styles.numberContainer}>
                    <View style={styles.iconContainer}>
                      {/* <WiFi /> */}
                    </View>
                    <Space width={10} />
                    <Text style={styles.numberText}>{service.lineIdentifier}</Text>
                    <Space width={10} />
                  </View>
                  <Space height={12} />
                </View>
                <Animated.View style={[styles.dials, { height: bigDialSize }]}>

                  <MoonDial
                    name={content.navigation.data}
                    formatValue={val => formatBytes(val, balance)}
                    formatRange={val => formatBytes(val, balance)}
                    size={bigDialSize}
                    value={getBalanceQuantity(balance)}
                    range={getBalanceStartingQuantity(balance)}
                    loading={loading}
                    onPress={() => void navigation.navigate('BroadbandData')}
                    style={styles.moondialStyle}
                    strokeColour={colors.srGreen + 20}
                    mostlyFullColour={colors.srDarkGreen}
                    almostEmptyColour={colors.srGreen}
                    mostlyFullTextColour={colors.srTextInput}
                    almostEmptyTextColour={colors.srTextInput}
                    textColour={colors.srTextInput}
										injectedContent={content}
                  />

                </Animated.View>
              </View>
            </View>
          </Animated.View>

          <Animated.View style={[styles.line, { opacity: show }]} />
        </View>

        <Space height={24} />

        <View style={styles.content}>
          <Space height={12} />
          {data.planProduct &&
            <View>
              <BasePlan
                  product={data.planProduct}
                  productContainer={styles.planContainer}
                  labelStyle={styles.descriptionStyle}
                  nameStyle={styles.descriptionContentStyle}
                  textStyle={styles.dateStyle}
                  buttonTitleStyle={styles.buttonTitleStyle}
                  buttonStyle={styles.buttonStyle}
                  productName={data.planProduct.product.productName}
                  price={data.planProduct.product.recurringCost}
                  code={data.planProduct.product.currency}
                  showChange={true}
									language={language}
									injectedContent={content}
              />
              <Space height={24} />
            </View>
          }

          {data.provisionedRecurringAddOns && data.provisionedRecurringAddOns.map((addOn, i) =>
            <React.Fragment key={i}>
              <ProductRecurring
                product={{
                  ...addOn.product,
                  expiration: addOn.expiryDateTime
                }}
                onEditProduct={() => void navigation.navigate('Plans')}
                onCancelProduct={() => { cancelRecurringAddOn(addOn) }}
                productContainer={styles.cashContainer}
                labelStyle={styles.descriptionStyle}
                nameStyle={styles.descriptionContentStyle}
                textStyle={styles.dateStyle}
                buttonTitleStyle={styles.buttonTitleStyle}
                buttonStyle={styles.buttonStyle}
                cancelStyle={styles.cancelStyle}
                addOn={addOn}
								language={language}
								injectedContent={content}
              />
              <Space height={24} />
            </React.Fragment>
          )}

          {data.provisionedOneOffAddOns && data.provisionedOneOffAddOns.map((addOn, i) =>
            <React.Fragment key={i}>
              <ProductAddon
                iconColor={colors.white}
                style={styles.addonStyle}
                placeholderStyle={styles.balanceItem}
                disabled={true}
                product={{
                  ...addOn.product,
                  expiration: addOn.expiryDateTime
                }}
								language={language}
								injectedContent={content}
              />
              <Space height={24} />
            </React.Fragment>
          )}
        </View>
      </Animated.ScrollView>
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    // flex: 1,
    width: Dimensions.get('window').width,
  },
  headerContainer: {
    justifyContent: 'center',
    height: 250,
    backgroundColor: colors.white,
  },
  headerContent: {
    justifyContent: 'center',
    ...adaptive.wrapperPadding,
    backgroundColor: colors.white,
    borderColor: colors.white,
    flex: 1
  },
  branding: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  dials: {
    flexDirection: 'row',
    justifyContent: 'space-evenly',
    alignItems: 'flex-end',
    zIndex: -1,
  },
  headerMain: {
    flexDirection: 'row',
    justifyContent: 'space-evenly'
  },
  line: {
    borderColor: colors.subtle,
    borderWidth: StyleSheet.hairlineWidth,
  },
  content: {
    paddingTop: 48,
    ...adaptive.wrapperPadding,
    paddingBottom: 24,
    zIndex: 0
  },
  moondialStyle: {
    ...typography.lightbold,
  },
  cashContainer: {
    padding: 24,
    backgroundColor: backgroundColors.srGreen,
    borderRadius: radius.medium,
    elevation: 8,
    ...shadows.shadowByElevation(8)
  },
  descriptionStyle: {
    fontSize: 16,
    lineHeight: 24,
    color: colors.white,
    ...typography.demi,
  },
  descriptionContentStyle: {
    fontSize: 18,
    lineHeight: 24,
    color: colors.white,
    ...typography.demi
  },
  dateStyle: {
    fontSize: 18,
    lineHeight: 24,
    color: colors.white,
    ...typography.demi
  },
  cancelStyle: {
    color: colors.white,
    textAlign: 'center'
  },
  addonStyle: {
    backgroundColor: colors.white
  },
  balanceItem: {
    backgroundColor: colors.srGreen
  },
  numberText: {
    textAlign: 'center',
    ...typography.bold,
    fontSize: 24,
    color: colors.srTextInput
  },
  amountDue: {
  },
  amountDueTitle: {
    textAlign: 'right',
    ...typography.bold,
    fontSize: 16,
    color: colors.srDarkGreen
  },
  amountDueText: {
    textAlign: 'right',
    ...typography.bold,
    fontSize: 20,
    color: colors.srDarkGreen
  },
  rowContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%'
  },
  numberContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
  },
  buttonStyle: {
    backgroundColor: colors.srDarkGreen
  },
  buttonTitleStyle: {
    ...typography.lightbold,
    fontSize: 20
  },
  iconContainer: {
    justifyContent: 'center'
  },
  planContainer: {
    padding: 24,
    backgroundColor: backgroundColors.srGreen,
    borderRadius: radius.medium,
    elevation: 8,
    ...shadows.shadowByElevation(8)
  }
})

